// ** React Imports
import {Suspense, lazy, useContext, Fragment, useEffect} from "react";

// ** Utils
import {useLayout} from "@src/utility/hooks/useLayout";
import {isUserLoggedIn, getUserData} from "@src/utility/Utils";
// import { AbilityContext } from '@src/utility/context/Can'
import {useRouterTransition} from "@src/utility/hooks/useRouterTransition";

// ** Custom Components
import LayoutWrapper from "@src/@core/layouts/components/layout-wrapper";

// ** Router Components
import {
    BrowserRouter as AppRouter,
    Route,
    Switch,
    Redirect,
    useLocation,
} from "react-router-dom"

// ** Layouts
import BlankLayout from "@src/@core/layouts/BlankLayout"
import VerticalLayout from "@src/layouts/VerticalLayout"
import HorizontalLayout from "@src/layouts/HorizontalLayout"

import {useSelector} from 'react-redux'

function verifyUserPermissions(userData, applicationGroupNames, routeMeta) {
    if (userData === null) return false
    let checkGroup, checkAtLestOnePermission

    if (userData.groups.length > 0) {
        // Is user in a valid group ?
        checkGroup = userData.groups.filter((userGroup) => applicationGroupNames.includes(userGroup.name))
        if (checkGroup.length === 0) return false
        else {
            // Check validity for this route.
            checkAtLestOnePermission = checkGroup.filter((valid_group) => routeMeta && routeMeta[valid_group.name])
            if (checkAtLestOnePermission.length > 0) return true
        }
    }
    // Is superuser ?
    if (userData.is_superuser) return true
    // Otherwise, Access Denied
    return false
}


const Router = (props) => {
    // ** Hooks
    const {layout, setLayout, setLastLayout} = useLayout();
    const {transition, setTransition} = useRouterTransition();
    // ** Routes & Default Routes
    const {defaultRoute, routes, user_groups} = props;

    // ** Default Layout
    const DefaultLayout =
        layout === "horizontal" ? "HorizontalLayout" : "VerticalLayout";

    // ** All of the available layouts
    const Layouts = {BlankLayout, VerticalLayout, HorizontalLayout};

    // ** Current Active Item
    const currentActiveItem = null;

    // ** Return Filtered Array of Routes & Paths
    const LayoutRoutesAndPaths = (layout) => {
        const LayoutRoutes = [];
        const LayoutPaths = [];

        if (routes) {
            routes.filter((route) => {
                // ** Checks if Route layout or Default layout matches current layout
                if (
                    route.layout === layout ||
                    (route.layout === undefined && DefaultLayout === layout)
                ) {
                    LayoutRoutes.push(route);
                    LayoutPaths.push(route.path);
                }
            });
        }

        return {LayoutRoutes, LayoutPaths};
    };

    const NotAuthorized = lazy(() => import("@src/views/NotAuthorized"));

    // ** Init Error Component
    const Error = lazy(() => import("@src/views/Error"));

    /**
     ** Final Route Component Checks for Login & User Role and then redirects to the route
     */
    const FinalRoute = (props) => {
        const location = useLocation()
        const route = props.route;
        const userData = getUserData();

        const appStore = useSelector(state => state.app)

        // ** Assign vars based on route meta
        if (route.meta) {
            // action = route.meta.action ? route.meta.action : null
            // resource = route.meta.resource ? route.meta.resource : null
        }

        // ** Listen to change events
        useEffect(() => {
            if (
                location &&
                appStore.appInfo.appWikiUrl &&
                document.querySelector('[data-id="userGuideButton"]') &&
                document.querySelector('[data-id="userGuideButton"] > a') &&
                document.querySelector('[data-id="userGuideButton"] > a').hasAttribute('href')
            ) {
                // Buttons:: 1- NormalUserGuideButton 2-OurThirdAppButton
                // Disable the default button and trigger an event on our button
                // let userGuidingNormalButton = document.querySelector('#userguiding-launcher')
                let userSupportButton = document.querySelector('[data-id="userGuideButton"]')
                let userSupportAnchor = document.querySelector('[data-id="userGuideButton"] > a')

                userSupportAnchor.removeAttribute('href')
                userSupportButton.addEventListener("click", (event) => {
                    // userGuidingNormalButton.remove()
                    window.open(appStore.appInfo.appWikiUrl,'_blank');
                    event.stopImmediatePropagation()
                });
            }
            if (
                location &&
                appStore.appInfo.appSupportUrl &&
                document.querySelector('[data-id="userSupportButton"]') &&
                document.querySelector('[data-id="userSupportButton"] > a') &&
                document.querySelector('[data-id="userSupportButton"] > a').hasAttribute('href')
            ) {
                // Buttons:: 1- NormalUserGuideButton 2-OurThirdAppButton
                // Disable the default button and trigger an event on our button
                // let userGuidingNormalButton = document.querySelector('#userguiding-launcher')
                let userSupportButton = document.querySelector('[data-id="userSupportButton"]')
                let userSupportAnchor = document.querySelector('[data-id="userSupportButton"] > a')

                userSupportAnchor.removeAttribute('href')
                userSupportButton.addEventListener("click", (event) => {
                    // userGuidingNormalButton.remove()
                    window.open(appStore.appInfo.appSupportUrl,'_blank');
                    event.stopImmediatePropagation()
                });
            }
        }, [location])

        // Must change password is an authRoute especially for users who are logging in for the first time
        // can be triggered only when redirect from login page (first time)
        // ** IF "must-change-password" route, Must not fall into line 205, but redirect here.
        if (
            (!isUserLoggedIn() || isUserLoggedIn()) &&
            route.path === '/must-change-password'
        ) {
            if (
                props.history &&
                props.history.location &&
                props.history.location.state &&
                props.history.location.state.challenge_name &&
                props.history.location.state.auth_session &&
                props.history.location.state.username
            ) {
                return <route.component {...props} />;
            } else {
                if (isUserLoggedIn())
                    return <Redirect to="/"/>;
                else if (!isUserLoggedIn())
                    return <Redirect to="/login"/>;
            }
        }

        if (
            (!isUserLoggedIn() && route.meta === undefined) ||
            (!isUserLoggedIn() &&
                route.meta &&
                !route.meta.authRoute &&
                !route.meta.publicRoute) ||
            (
                isUserLoggedIn() &&
                (!userData.appIdentifier ||
                    userData.appIdentifier !== appStore.appInfo.appIdentifier)
            )
        ) {
            /**
             ** If user is not Logged in & route meta is undefined
             ** OR
             ** If user is not Logged in & route.meta.authRoute, !route.meta.publicRoute are undefined
             ** OR
             ** If users is LOGGED but has not the appIdentifier equal to the actual appIdentifier
             ** Then redirect user to login
             */
            let shouldResetStorage = isUserLoggedIn() && (!userData.appIdentifier || userData.appIdentifier !== appStore.appInfo.appIdentifier)
            if (shouldResetStorage) {
                localStorage.removeItem('userData')
            }
            // Remember this {...props} to backup the searched url and redirect the user after login
            // -------- LAST ROUTE IS NOT NECESSARY -------
            // if (props.match && props.match.url && props.location.pathname)
            //     localStorage.setItem('lastRoute', JSON.stringify(
            //         `${props.location.pathname}${props.location.search}`
            //     ))
            return <Redirect to="/login"/>;
        } else if (route.path === '/logout') {
            localStorage.removeItem('userData')
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            return <Redirect to="/login"/>;
        } else if (route.meta && route.meta.authRoute && isUserLoggedIn()) {
            return <Redirect to="/"/>;
        } else if (
            isUserLoggedIn() && userData !== null && !verifyUserPermissions(userData, user_groups, route.meta) && !route.meta.publicRoute
        ) {
            return <Redirect to="/misc/not-authorized"/>;
        } else {
            // ** If none of the above render component
            // ** If user was landing to any route, but redirected into /login route, then redirect him to the last cached url
            //
            // -------- LAST ROUTE IS NOT NECESSARY -------
            // if (
			// 	localStorage.getItem('lastRoute') && route.meta && !route.meta.authRoute
			// 	&& verifyUserPermissions(userData, user_groups, route.meta)
			// ) {
            //     const oldRoute = JSON.parse(localStorage.getItem('lastRoute'))
            //     localStorage.removeItem('lastRoute')
            //     return <Redirect to={oldRoute} />;
            // }
            //
            // ** Else render component page
            return <route.component {...props} />;
        }
    };

    // ** Return Route to Render
    const ResolveRoutes = () => {
        return Object.keys(Layouts).map((layout, index) => {
            // ** Convert Layout parameter to Layout Component
            // ? Note: make sure to keep layout and component name equal

            const LayoutTag = Layouts[layout];

            // ** Get Routes and Paths of the Layout
            const {LayoutRoutes, LayoutPaths} = LayoutRoutesAndPaths(layout);

            // ** We have freedom to display different layout for different route
            // ** We have made LayoutTag dynamic based on layout, we can also replace it with the only layout component,
            // ** that we want to implement like VerticalLayout or HorizontalLayout
            // ** We segregated all the routes based on the layouts and Resolved all those routes inside layouts

            // ** RouterProps to pass them to Layouts
            const routerProps = {
                user_groups
            }

            return (
                <Route path={LayoutPaths} key={index}>
                    <LayoutTag
                        layout={layout}
                        setLayout={setLayout}
                        transition={transition}
                        routerProps={routerProps}
                        setLastLayout={setLastLayout}
                        setTransition={setTransition}
                        currentActiveItem={currentActiveItem}
                    >
                        <Switch>
                            {LayoutRoutes.map((route) => {
                                return (
                                    <Route
                                        key={route.path}
                                        path={route.path}
                                        exact={route.exact === true}
                                        render={(props) => {
                                            // ** Assign props to routerProps
                                            Object.assign(props, {
                                                ...props,
                                                meta: route.meta,
                                            });

                                            return (
                                                <Fragment>
                                                    {/* Layout Wrapper to add classes based on route's layout, appLayout and className */}

                                                    {route.layout === "BlankLayout" ? (
                                                        <Fragment>
                                                            <FinalRoute route={route} {...props} />
                                                        </Fragment>
                                                    ) : (
                                                        <LayoutWrapper
                                                            layout={DefaultLayout}
                                                            transition={transition}
                                                            setTransition={setTransition}
                                                            /* Conditional props */
                                                            /*eslint-disable */
                                                            {...(route.appLayout
                                                                ? {
                                                                    appLayout: route.appLayout,
                                                                }
                                                                : {})}
                                                            {...(route.meta
                                                                ? {
                                                                    routeMeta: route.meta,
                                                                }
                                                                : {})}
                                                            {...(route.className
                                                                ? {
                                                                    wrapperClass: route.className,
                                                                }
                                                                : {})}
                                                            /*eslint-enable */
                                                        >
                                                            <Suspense fallback={null}>
                                                                <FinalRoute route={route} {...props} />
                                                            </Suspense>
                                                        </LayoutWrapper>
                                                    )}
                                                </Fragment>
                                            );
                                        }}
                                    />
                                );
                            })}
                        </Switch>
                    </LayoutTag>
                </Route>
            );
        });
    };

    return (
        <AppRouter>
            <Switch>
                {/* If user is logged in Redirect user to DefaultRoute else to login */}
                <Route
                    exact
                    path="/"
                    render={() => {
                        return <Redirect to={defaultRoute}/>;
                    }}
                />
                {/* Not Auth Route */}
                <Route
                    exact
                    path="/misc/not-authorized"
                    render={() => (
                        <Layouts.BlankLayout>
                            <NotAuthorized/>
                        </Layouts.BlankLayout>
                    )}
                />
                {ResolveRoutes()}

                {/* NotFound Error page */}
                <Route path="*" component={Error}/>
            </Switch>
        </AppRouter>
    );
};

export {Router, verifyUserPermissions}
